import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

const StructureSetApprovalStatusConfirmationDialog = ({
    onApprovalStatusChange,
}: {
    onApprovalStatusChange: (status: DICOMApprovalStatus) => void;
}) => {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const [
        openedStructureSetApprovalConfirmationDialog,
        setOpenedStructureSetApprovalConfirmationDialog,
    ] = useRecoilState(openedStructureSetApprovalConfirmationDialogState);

    const handleStructureSetApprovalStatusChangeConfirmation = () => {
        if (openedStructureSetApprovalConfirmationDialog) {
            onApprovalStatusChange(
                openedStructureSetApprovalConfirmationDialog?.newApprovalStatus
            );
            setOpenedStructureSetApprovalConfirmationDialog(null);
        }
    };

    const handleStructureSetApprovalStatusChangeDialogCancel = () => {
        setOpenedStructureSetApprovalConfirmationDialog(null);
    };

    const isApprovingStructureSet =
        openedStructureSetApprovalConfirmationDialog?.newApprovalStatus ===
        DICOMApprovalStatus.APPROVED;

    const rejectedRois = isApprovingStructureSet
        ? (currentStructureSet?.roiArray.filter(
              (roi) => roi.approvalStatus === DICOMApprovalStatus.REJECTED
          ) ?? [])
        : [];

    const hasRejectedRois = rejectedRois.length > 0;

    const baseMessage = (
        <FormattedMessage
            id="structureSetApprovalStatusChangeDescription"
            values={{
                newApprovalStatus: (
                    <strong>
                        {
                            openedStructureSetApprovalConfirmationDialog?.newApprovalStatus
                        }
                    </strong>
                ),
            }}
        />
    );

    const message = hasRejectedRois ? (
        <>
            {baseMessage}
            <br />
            <br />
            <FormattedMessage
                id="structureSetApproveOverridesRejectedWarning"
                values={{
                    rejectedNames: (
                        <strong>
                            {rejectedRois.map((r) => r.name).join(', ')}
                        </strong>
                    ),
                }}
            />
        </>
    ) : (
        baseMessage
    );

    return (
        openedStructureSetApprovalConfirmationDialog && (
            <ConfirmationDialog
                open={
                    openedStructureSetApprovalConfirmationDialog.sopUid ===
                    currentStructureSet?.dicomUid
                }
                onCancel={handleStructureSetApprovalStatusChangeDialogCancel}
                onConfirm={handleStructureSetApprovalStatusChangeConfirmation}
                title={<FormattedMessage id="confirmApprovalStatusChange" />}
                message={message}
                cancelText={
                    hasRejectedRois ? (
                        <FormattedMessage id="goBack" />
                    ) : (
                        <FormattedMessage id="cancel" />
                    )
                }
                confirmText={<FormattedMessage id="confirm" />}
                mode={hasRejectedRois ? 'warning' : 'secondary'}
            />
        )
    );
};

export default StructureSetApprovalStatusConfirmationDialog;
